import React from "react";
import { SelectableLink, SelectableLinkContainer } from "components/Link";
import NumberInput from "components/NumberInput";
import GameText from "containers/Game/GameText";
import {
  DEFAULT_ROLE_CONFIG,
  MAX_PLAYERS,
  MIN_PLAYERS,
  MIN_VAMPIRES,
} from "core/constants";
import { red, white } from "core/styles";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { ButtonContainer, StyledButton } from "./Button/v2";

const SettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  font-size: 1.2em;
  margin: 0.5em 0;
  text-align: center;
  margin: 1em;
`;

const WarningMessage = styled.div`
  color: #ffa500;
  font-size: 1.2em;
  margin: 0.5em 0;
  text-align: center;
  margin: 1em;
`;

const InfoMessage = styled.div`
  color: ${white};
  font-size: 1.2em;
  margin: 0.5em 0;
  text-align: center;
  margin: 1em;
`;

const TitleVIP = styled.h3`
  color: ${white};
  font-size: 2.2em;
  margin-bottom: 1.5em;
`;

const SectionTitleVIP = styled.h4`
  color: ${red};
  font-size: 1.5em;
  margin: 1.2em 0;
  text-align: center;
`;
const SectionTitle = styled.h4`
  font-size: 1.2em;
  margin: 0.8em 0 0.8em 0;
  text-align: center;
`;

const STORAGE_KEY = "vampireParty_gameSettings";

const DEFAULT_TIMERS = {
  night: 30,
  day: 180,
  morning: 20,
  directory: 20,
  polls: 15,
};

const GameSettings = ({ settings, onSave, isVIP, isLocked, onClickBack }) => {
  const [timers, setTimers] = useState({
    ...DEFAULT_TIMERS,
    ...settings?.timers,
  });
  const [roleConfig, setRoleConfig] = useState(() => ({
    ...DEFAULT_ROLE_CONFIG,
    ...(settings?.roleConfig || {}),
  }));
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [info, setInfo] = useState("");
  const [vipAutoUpdate, setVipAutoUpdate] = useState("");

  // Sync local state when settings prop changes (e.g., from server broadcast)
  useEffect(() => {
    if (settings?.timers) {
      setTimers({ ...DEFAULT_TIMERS, ...settings.timers });
    }
    if (settings?.roleConfig) {
      setRoleConfig({ ...DEFAULT_ROLE_CONFIG, ...settings.roleConfig });
    }
  }, [settings, settings?.timers, settings?.roleConfig]);

  // Auto-correct settings at game start if needed
  useEffect(() => {
    if (!isVIP || !settings) return;
    let updated = false;
    let newConfig = { ...roleConfig };
    // Example: If all players are vampires, reduce vampires to min
    if (
      newConfig.maxPlayers === newConfig.vampires &&
      newConfig.vampires > MIN_VAMPIRES
    ) {
      newConfig.vampires = MIN_VAMPIRES;
      updated = true;
    }
    // Add more auto-correction rules as needed
    if (updated) {
      setRoleConfig(newConfig);
      setVipAutoUpdate(
        "Game settings were auto-updated to ensure playability. Please review and update your settings if needed."
      );
    }
  }, [isVIP, settings]);
  // Load saved settings from local storage on mount
  useEffect(() => {
    const loadSavedSettings = () => {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          const { roleConfig: savedRoleConfig } = JSON.parse(saved);
          if (savedRoleConfig && !settings?.roleConfig) {
            setRoleConfig(savedRoleConfig);
          }
        }
      } catch (error) {
        console.error("Failed to load saved settings:", error);
      }
    };
    loadSavedSettings();
  }, []);

  useEffect(() => {
    validateRoleConfig(roleConfig);
  }, [roleConfig]);

  const validateRoleConfig = (config) => {
    const {
      minPlayers,
      maxPlayers,
      vampires,
      garlicFarmers,
      priests,
      librarians,
      mayors,
      cullens,
    } = config;

    // Calculate total configured roles
    const totalSpecialRoles =
      vampires + garlicFarmers + priests + librarians + mayors + cullens;

    // Clear previous messages
    setError("");
    setWarning("");
    setInfo("");

    // Validate minimum requirements
    if (minPlayers < MIN_PLAYERS) {
      setError(`Minimum ${MIN_PLAYERS} players required`);
      return false;
    }

    if (maxPlayers < MIN_PLAYERS) {
      setError(`Maximum must be at least ${MIN_PLAYERS} players`);
      return false;
    }

    if (minPlayers > maxPlayers) {
      setError(`Minimum players cannot exceed maximum players`);
      return false;
    }

    if (vampires < MIN_VAMPIRES) {
      setError(`Minimum ${MIN_VAMPIRES} vampire required`);
      return false;
    }

    // Check if total special roles exceeds max players
    if (totalSpecialRoles > maxPlayers) {
      setError(
        `Too many special roles! Total configured roles (${totalSpecialRoles}) exceeds max players (${maxPlayers}).`
      );
      return false;
    }

    // Calculate remaining humans
    const remainingHumans = maxPlayers - totalSpecialRoles;
    if (remainingHumans > 0) {
      setInfo(
        `${remainingHumans} player${
          remainingHumans !== 1 ? "s" : ""
        } will be assigned as regular humans`
      );
    } else if (remainingHumans === 0) {
      setWarning("No regular humans - all players will have special roles");
    }

    // Warning if configured roles are very close to max players
    if (totalSpecialRoles > maxPlayers * 0.8 && remainingHumans > 0) {
      setWarning("High ratio of special roles - game may be unbalanced");
    }

    return true;
  };

  const handleTimerChange = (phase, value) => {
    setError(""); // Clear error on change
    setTimers({ ...timers, [phase]: parseInt(value) || 0 });
  };

  const handleRoleConfigChange = (role, value) => {
    const numValue = parseInt(value) || 0;
    setRoleConfig({ ...roleConfig, [role]: Math.max(0, numValue) });
  };

  const handleResetToDefaults = () => {
    setTimers(DEFAULT_TIMERS);
    setRoleConfig(DEFAULT_ROLE_CONFIG);
    setError("");
    setWarning("");
    setInfo("");
    // Trigger validation for the default config
    setTimeout(() => validateRoleConfig(DEFAULT_ROLE_CONFIG), 0);
  };

  const handleSaveToLocalStorage = () => {
    try {
      const settingsToSave = {
        roleConfig,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settingsToSave));
      setInfo("Settings saved locally!");
      setTimeout(() => {
        if (info === "Settings saved locally!") setInfo("");
      }, 2000);
    } catch (error) {
      setError("Failed to save settings locally");
      console.error("Failed to save to local storage:", error);
    }
  };

  const handleSave = () => {
    // Validate timers
    const invalidTimers = Object.entries(timers).filter(
      ([_, value]) => value < 5 || value > 600
    );

    if (invalidTimers.length > 0) {
      setError("All timers must be between 5 and 600 seconds");
      return;
    }

    // Validate role configuration
    if (!validateRoleConfig(roleConfig)) {
      return;
    }

    setError("");

    // Save to local storage when saving settings
    try {
      const settingsToSave = {
        roleConfig,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settingsToSave));
    } catch (error) {
      console.error("Failed to save to local storage:", error);
    }

    onSave({ timers, roleConfig });

    setTimeout(() => {
      onClickBack();
    }, 250);
  };

  if (!settings) {
    return null;
  }

  // Ensure roleConfig has defaults
  const displayRoleConfig = settings.roleConfig || DEFAULT_ROLE_CONFIG;

  if (!isVIP) {
    // Non-VIP players see read-only view
    const totalSpecialRoles =
      (displayRoleConfig.vampires || 0) +
      (displayRoleConfig.garlicFarmers || 0) +
      (displayRoleConfig.priests || 0) +
      (displayRoleConfig.librarians || 0) +
      (displayRoleConfig.mayors || 0) +
      (displayRoleConfig.cullens || 0);
    const regularHumans =
      (displayRoleConfig.maxPlayers || 0) - totalSpecialRoles;

    return (
      <SettingsContainer data-test-id="game-settings-panel">
        <SelectableLinkContainer>
          <SelectableLink to="/" onClick={onClickBack} backButton />
        </SelectableLinkContainer>
        <GameText title={"Settings"} isLong>
          <SectionTitle>Role Config</SectionTitle>

          <div data-test-id="role-minPlayers-display">
            Min Players: {displayRoleConfig.minPlayers || MIN_PLAYERS}
          </div>
          <div data-test-id="role-maxPlayers-display">
            Max Players: {displayRoleConfig.maxPlayers}
          </div>
          <div data-test-id="role-vampires-display">
            Vampires: {displayRoleConfig.vampires}
          </div>
          <div data-test-id="role-garlicFarmers-display">
            Garlic Farmers: {displayRoleConfig.garlicFarmers}
          </div>
          <div data-test-id="role-priests-display">
            Priests: {displayRoleConfig.priests}
          </div>
          <div data-test-id="role-librarians-display">
            Librarians: {displayRoleConfig.librarians}
          </div>
          <div data-test-id="role-mayors-display">
            Mayors: {displayRoleConfig.mayors}
          </div>
          <div data-test-id="role-cullens-display">
            Cullens: {displayRoleConfig.cullens}
          </div>
          <div data-test-id="role-humans-display">Humans: {regularHumans}</div>
          <SectionTitle>Phase Timers</SectionTitle>

          <div data-test-id="timer-night-display">
            Night: {settings.timers.night}s
          </div>
          <div data-test-id="timer-day-display">
            Day: {settings.timers.day}s
          </div>
          <div data-test-id="timer-morning-display">
            Morning: {settings.timers.morning}s
          </div>
          <div data-test-id="timer-directory-display">
            Directory: {settings.timers.directory}s
          </div>
          <div data-test-id="timer-polls-display">
            Poll Results: {settings.timers.polls}s
          </div>
        </GameText>
      </SettingsContainer>
    );
  }

  return (
    <SettingsContainer data-test-id="game-settings-panel">
      {vipAutoUpdate && (
        <WarningMessage data-test-id="vip-auto-update-warning">
          {vipAutoUpdate}
        </WarningMessage>
      )}
      <SelectableLinkContainer>
        <SelectableLink to="/" onClick={onClickBack} backButton />
      </SelectableLinkContainer>
      <TitleVIP>Game Settings</TitleVIP>

      <SectionTitleVIP>Role Config</SectionTitleVIP>

      <NumberInput
        label={`Min Players`}
        value={roleConfig.minPlayers ?? MIN_PLAYERS}
        onChange={(v) => handleRoleConfigChange("minPlayers", v)}
        disabled={isLocked}
        type="number"
        min={MIN_PLAYERS}
        max={MAX_PLAYERS}
        dataTestId="role-minPlayers"
      />

      <NumberInput
        label={`Max Players`}
        value={roleConfig.maxPlayers}
        onChange={(v) => handleRoleConfigChange("maxPlayers", v)}
        disabled={isLocked}
        type="number"
        min={MIN_PLAYERS}
        max={MAX_PLAYERS}
        dataTestId="role-maxPlayers"
      />

      <NumberInput
        label={`Vampires`}
        value={roleConfig.vampires}
        onChange={(v) => handleRoleConfigChange("vampires", v)}
        disabled={isLocked}
        type="number"
        min={MIN_VAMPIRES}
        max={roleConfig.maxPlayers - 1}
        dataTestId="role-vampires"
      />

      <NumberInput
        label="Garlic Farmers"
        value={roleConfig.garlicFarmers}
        onChange={(v) => handleRoleConfigChange("garlicFarmers", v)}
        disabled={isLocked}
        type="number"
        min={0}
        max={roleConfig.maxPlayers}
        dataTestId="role-garlicFarmers"
      />

      <NumberInput
        label="Priests"
        value={roleConfig.priests}
        onChange={(v) => handleRoleConfigChange("priests", v)}
        disabled={isLocked}
        type="number"
        min={0}
        max={roleConfig.maxPlayers}
        dataTestId="role-priests"
      />

      <NumberInput
        label="Librarians"
        value={roleConfig.librarians}
        onChange={(v) => handleRoleConfigChange("librarians", v)}
        disabled={isLocked}
        type="number"
        min={0}
        max={roleConfig.maxPlayers}
        dataTestId="role-librarians"
      />

      <NumberInput
        label="Mayors"
        value={roleConfig.mayors}
        onChange={(v) => handleRoleConfigChange("mayors", v)}
        disabled={isLocked}
        type="number"
        min={0}
        max={roleConfig.maxPlayers}
        dataTestId="role-mayors"
      />

      <NumberInput
        label="Cullens"
        value={roleConfig.cullens ?? 0}
        onChange={(v) => handleRoleConfigChange("cullens", v)}
        disabled={isLocked}
        type="number"
        min={0}
        max={roleConfig.maxPlayers}
        dataTestId="role-cullens"
      />

      <SectionTitleVIP>Phase Timers</SectionTitleVIP>

      <NumberInput
        label="Night"
        value={timers.night}
        onChange={(v) => handleTimerChange("night", v)}
        disabled={isLocked}
        type="number"
        min={5}
        max={600}
        dataTestId="timer-night"
      />

      <NumberInput
        label="Day/Voting"
        value={timers.day}
        onChange={(v) => handleTimerChange("day", v)}
        disabled={isLocked}
        type="number"
        min={5}
        max={600}
        dataTestId="timer-day"
      />

      <NumberInput
        label="Morning"
        value={timers.morning}
        onChange={(v) => handleTimerChange("morning", v)}
        disabled={isLocked}
        type="number"
        min={5}
        max={600}
        dataTestId="timer-morning"
      />

      <NumberInput
        label="Directory"
        value={timers.directory}
        onChange={(v) => handleTimerChange("directory", v)}
        disabled={isLocked}
        type="number"
        min={5}
        max={600}
        dataTestId="timer-directory"
      />

      <NumberInput
        label="Poll"
        value={timers.polls}
        onChange={(v) => handleTimerChange("polls", v)}
        disabled={isLocked}
        type="number"
        min={5}
        max={600}
        dataTestId="timer-polls"
      />

      {info && <InfoMessage data-test-id="settings-info">{info}</InfoMessage>}
      {warning && (
        <WarningMessage data-test-id="settings-warning">
          {warning}
        </WarningMessage>
      )}
      {error && (
        <ErrorMessage data-test-id="settings-error">{error}</ErrorMessage>
      )}

      {!isLocked && (
        <ButtonContainer>
          <StyledButton
            data-test-id="save-settings-button"
            onClick={handleSave}
          >
            Save Settings
          </StyledButton>

          <StyledButton
            data-test-id="reset-defaults-button"
            onClick={handleResetToDefaults}
          >
            Reset to Defaults
          </StyledButton>
        </ButtonContainer>
      )}
    </SettingsContainer>
  );
};

export default GameSettings;
